import { useEffect, useRef, useState } from "react";
import { takeScroll } from "../utils/scrollTarget";
import { T, UI, BTN, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import Money from "./Money";
import { Card } from "./Card";
import SymbolAutocomplete from "./SymbolAutocomplete";
import Icon from "./Icon";
import LTR from "./LTR";
import { noQuoteText } from "../utils/quoteState";

// ─── רשימת מעקב · in the portfolio screen (sprint 13, 5.8) ──────────────────
// Stocks that are not held. News and geopolitical events about them are
// marked "במעקב שלך". A registered user adds and removes symbols here; the
// list is kept in their account (lib/watchlistService.js). A guest sees the
// demo list, which is not saved. No ranking, no suggestion: the list is only
// what the user typed.
export default function WatchlistSection({ watch, cur }) {
  const [sym, setSym] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  // Arriving from the home screen's "כל רשימת המעקב": scroll to this section,
  // whether the screen was already mounted (the event) or not (on mount).
  const ref = useRef(null);
  // On mount the scroll waits a beat: App resets the page to the top when
  // the screen changes, and its effect runs after this one (review).
  useEffect(() => {
    const go = () => { if (takeScroll("watchlist")) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
    const t = setTimeout(go, 450);
    const onEvent = e => { if (e.detail?.id === "watchlist") go(); };
    window.addEventListener("si-scroll-to", onEvent);
    return () => { clearTimeout(t); window.removeEventListener("si-scroll-to", onEvent); };
  }, []);
  if (!watch) return null;
  const { list, canEdit, isGuest, add, remove } = watch;

  const submit = async () => {
    const s = sym.trim().toUpperCase();
    if (!s) return;
    setBusy(true); setNote(null);
    const res = await add(s);
    setBusy(false);
    if (res === true) setSym("");
    else if (res && res.failed) setNote("השמירה נכשלה · אפשר לנסות שוב");
    else setNote(list.some(w => w.symbol === s) ? "הסימול כבר ברשימה" : "הסימול בתיק, ולכן אינו נכנס לרשימת המעקב");
  };

  return (
    <div ref={ref} id="si-watchlist" style={{ scrollMarginTop: 80 }}>
    <Card style={{ padding: "16px 18px" }}>
      <h2 style={{ ...TYPE.heading, margin: 0 }}>רשימת מעקב</h2>
      <div style={{ ...TYPE.caption, marginTop: 6, fontSize: 12.5, lineHeight: 1.6 }}>
        מניות שאינן בתיק · חדשות ואירועים עליהן מסומנים "במעקב שלך"
      </div>

      {list.length === 0 ? (
        <div style={{ ...TYPE.bodySmall, marginTop: 12 }}>הרשימה ריקה</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
          {list.map(w => (
            <div key={w.symbol} data-watch={w.symbol} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${UI.line.hairline}` }}>
              <button onClick={() => window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol: w.symbol } }))}
                style={{ background: "transparent", border: "none", padding: 0, minHeight: 44, cursor: "pointer", fontFamily: "inherit", flex: 1, textAlign: "start" }}>
                <LTR style={{ fontSize: 15, fontWeight: 800, color: UI.text.primary, fontFamily: FONT.latin }}>{w.symbol}</LTR>
              </button>
              {w.currentPrice > 0
                ? <Money value={w.currentPrice} cur={cur} size={14} weight={700}/>
                : <LTR className="si-num" style={{ fontSize: 14, fontWeight: 700, color: UI.text.muted }}>{w.lastQuoteAt ? noQuoteText(w) : "—"}</LTR>}
              {canEdit && (
                <button onClick={() => remove(w.symbol)} aria-label={`הסר את ${w.symbol} מרשימת המעקב`}
                  style={{ background: "transparent", border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.md, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <Icon name="x" size={13} color={UI.text.secondary}/>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, alignItems: "end", marginTop: 12 }}>
          <SymbolAutocomplete value={sym} onChange={setSym} label="הוספת סימול" placeholder="למשל MSFT"
            inputStyle={{ width: "100%", boxSizing: "border-box", minHeight: 44, padding: "8px 12px", borderRadius: T.radius.md, border: `1px solid ${UI.line.strong}`, background: UI.surface.sunken, color: UI.text.primary, fontSize: 15, fontFamily: "inherit" }}/>
          <button onClick={submit} disabled={busy || !sym.trim()}
            style={{ ...BTN.secondary, minHeight: 44, padding: "10px 16px", opacity: busy || !sym.trim() ? 0.6 : 1 }}>
            הוסף
          </button>
        </div>
      ) : (
        <div style={{ ...TYPE.caption, marginTop: 12 }}>
          {isGuest ? "במצב אורח זו רשימה לדוגמה, והיא לא נשמרת." : "שמירת רשימת המעקב עדיין אינה זמינה בחשבון."}
        </div>
      )}
      {note && <div role="status" style={{ ...TYPE.caption, marginTop: 8 }}>{note}</div>}
    </Card>
    </div>
  );
}
